import { Box, Chip, Dialog, DialogContent, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material"
import { AddCircleRounded, DeleteRounded, LinkOffRounded, LockRounded } from "@mui/icons-material"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import { BoxFC, BoxFR } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { alertConfirm, alertConfirmDelete, alertError, alertSuccess } from "components/Alert"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { blue, grey, yellow } from "@mui/material/colors"
import { numberFormat, numberFormatNoFlotingPoint } from "utils"
import { PAYROLL_STS } from "./initData"
import { buildSlipData, slipDetailRows, slipTotalRows } from "./slipData"
import ClaimAccrualDialog from "./ClaimAccrualDialog"

const initAdd = { ExpId: "", Amnt: "", Dscp: "" }
const MOVABLE_EXP = [89, 90, 91] // trip legs — the only lines whose งวดที่จ่าย can be moved
const TRIP_TYP = 6 // expense_type of the ค่าเที่ยว lines shown in the grid
const EMPTY_ROW = {}

const slipBoxSx = {
  flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
  border: `1px solid ${grey[400]}`, borderRadius: 1, overflow: "hidden",
}
const slipRowSx = { display: "flex", justifyContent: "space-between", gap: 1, px: 1, py: 0.15 }

// one payslip box (header / rows / bold footer total) — the same four boxes drawn by
// printDriverSalaryV3Summary, so the dialog and the printed payslip read identically
const SlipBox = ({ title, rows, footerLabel, footerAmnt, footerMinus }) => (
  <Box sx={slipBoxSx}>
    <Box sx={{ bgcolor: blue[100], py: 0.3, textAlign: "center" }}>
      <Typography variant="body2" fontWeight="bold">{title}</Typography>
    </Box>
    <Box sx={{ flex: 1, py: 0.5 }}>
      {rows.map((r, i) => (
        <Box key={i} sx={slipRowSx}>
          <Typography variant="caption" noWrap>{r.label || "-"}</Typography>
          <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
            {r.isDetail
              ? `${r.value ? numberFormatNoFlotingPoint(+r.value) : "-"} ${r.unit}`
              : `${r.minus ? "- " : ""}${numberFormat(+(r.amnt || 0))} บาท`}
          </Typography>
        </Box>
      ))}
    </Box>
    {footerLabel != null &&
      <Box sx={{ ...slipRowSx, py: 0.3, bgcolor: yellow[100], borderTop: `1px solid ${grey[400]}` }}>
        <Typography variant="caption" fontWeight="bold" noWrap>{footerLabel}</Typography>
        <Typography variant="caption" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
          {`${footerMinus ? "- " : ""}${numberFormat(+(footerAmnt || 0))} บาท`}
        </Typography>
      </Box>}
  </Box>
)

const PayrollDetailDialog = ({ dialogOpen, setDialogOpen, drvId, salDte, drvNm,
  gridRow = EMPTY_ROW, expColumns = [], onFinish }) => {
  const { ax } = useContext(GlobalStateContext)
  const [detail, setDetail] = useState({ payroll: null, items: [] })
  const [addData, setAddData] = useState({ ...initAdd })
  const [dialogClaimOpen, setDialogClaimOpen] = useState(false)

  const load = useCallback(() => {
    ax.post("/branch/driver-payroll-v3/getPayrollDetail", {
      DrvId: drvId, SalDte: dayjs(salDte).format("YYYY-MM-DD"),
    }).then(value => { if (value.data) setDetail(value.data) })
  }, [ax, drvId, salDte])

  useEffect(() => { if (dialogOpen) { load(); setAddData({ ...initAdd }) } }, [dialogOpen, load])

  const payroll = detail.payroll
  const sts = payroll?.PayrollSts ?? 0
  const locked = sts >= 2
  // top fixed section: the same รายได้ / รายการหัก / สรุปยอด / ข้อมูลสรุป boxes as the printed payslip
  const slip = useMemo(
    () => buildSlipData({ gridRow, payroll: detail.payroll || {}, items: detail.items }, expColumns),
    [gridRow, detail, expColumns])
  // the grid shows only the trip lines (ค่าเที่ยว, type 6) — every other component is a box above
  const rows = useMemo(
    () => detail.items.filter(it => it.ExpTypId === TRIP_TYP).map(it => ({ ...it, id: it.WgItmId })), [detail.items])
  // the "เพิ่มรายการ" list comes from the expense master the backend sent, so it can never drift
  // from the ExpIds this branch actually has
  const addOptions = useMemo(() => expColumns.filter(c => c.kind === "exp"), [expColumns])

  const handleAdd = useCallback(() => {
    if (!payroll) { alertError("ไม่พบงวดเงินเดือน กรุณาสร้างงวดก่อน"); return }
    if (!addData.ExpId || !addData.Amnt) { alertError("กรุณาระบุรายการและจำนวนเงิน"); return }
    ax.post("/branch/driver-payroll-v3/insertWageItem", {
      DrvId: drvId, ExpId: addData.ExpId, Amnt: +addData.Amnt,
      PayrollId: payroll.PayrollId, SalDte: dayjs(salDte).format("YYYY-MM-DD"), Dscp: addData.Dscp || null,
    }).then(value => { if (value.data != null) { setAddData({ ...initAdd }); load(); if (onFinish) onFinish() } })
  }, [ax, addData, payroll, drvId, salDte, load, onFinish])

  const handleDelItem = useCallback((it) => {
    alertConfirmDelete(() => {
      ax.post("/branch/driver-payroll-v3/deleteWageItem", { WgItmId: it.WgItmId })
        .then(value => { if (value.data) { load(); if (onFinish) onFinish() } })
    })
  }, [ax, load, onFinish])

  // a claimed line was entered as its own ค้างจ่าย document — release it instead of deleting it
  const handleUnclaim = useCallback((it) => {
    alertConfirm(`เอา "${it.ExpNm}" ออกจากงวดนี้ (กลับเป็นค้างจ่าย)?`, () => {
      ax.post("/branch/driver-payroll-v3/unclaimWageItems", { WgItmIds: [it.WgItmId] }).then(value => {
        if (!value.data) return
        if (value.data.unclaimed) alertSuccess("เอาออกจากงวดแล้ว")
        else alertError(value.data.warnings.map(w => w.message).join("\n"))
        load(); if (onFinish) onFinish()
      })
    })
  }, [ax, load, onFinish])

  // move one trip leg's wage into another month's payroll (the leg then leaves this งวด)
  const handleMove = useCallback((row, v) => {
    if (!v || dayjs(v).isSame(dayjs(row.SalDte), "month")) return
    alertConfirm(`ย้าย "${row.ExpNm}" ไปงวด ${dayjs(v).format("MM/YYYY")}?`, () => {
      ax.post("/branch/driver-payroll-v3/moveWageItemMonth", {
        WgItmId: row.WgItmId, ToSalDte: dayjs(v).format("YYYY-MM-DD"),
      }).then(value => { if (value.data) { alertSuccess("ย้ายงวดแล้ว"); load(); if (onFinish) onFinish() } })
    })
  }, [ax, load, onFinish])

  const doAction = useCallback((path, body, okMsg) => {
    ax.post(path, body).then(value => {
      if (value.data) { alertSuccess(okMsg); load(); if (onFinish) onFinish() }
    })
  }, [ax, load, onFinish])

  const handleConfirm = () => alertConfirm("เมื่อยืนยันแล้วจะแก้ไขรายการไม่ได้ ต้องการยืนยันเงินเดือน?",
    () => doAction("/branch/driver-payroll-v3/confirmPayroll", { PayrollId: payroll.PayrollId }, "ยืนยันเงินเดือนแล้ว"))
  const handlePay = () => alertConfirm("ยืนยันการจ่ายเงินเดือน?",
    () => doAction("/branch/driver-payroll-v3/payPayroll",
      { PayrollId: payroll.PayrollId, PayDte: dayjs().format("YYYY-MM-DD HH:mm:ss") }, "จ่ายเงินเดือนแล้ว"))
  const handleReopen = () => alertConfirm("เปิดงวดนี้กลับเป็นร่าง?",
    () => doAction("/branch/driver-payroll-v3/reopenPayroll", { PayrollId: payroll.PayrollId }, "เปิดแก้ไขแล้ว"))

  const stsInfo = PAYROLL_STS[sts] || PAYROLL_STS[0]

  const columns = useMemo(() => [
    { field: "DrvNm", headerName: "พขร.", width: 100 },
    {
      field: "StsTm", headerName: "วันที่วิ่งงาน", width: 110,
      valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY HH:mm") : "-",
    },
    {
      field: "ExpNm", headerName: "รายการ", flex: 1, minWidth: 180,
      renderCell: ({ row }) => (
        <BoxFR sx={{ gap: 0.5 }}>
          <Typography variant="body2">{row.ExpNm}{row.ContNo ? ` (${row.ContNo})` : ""}</Typography>
          {+row.IsClaimed === 1 &&
            <Tooltip title="ลงไว้เป็นค้างจ่าย แล้วถูกดึงเข้างวดนี้">
              <Chip size="small" variant="outlined" color="info" label="ค้างจ่าย" />
            </Tooltip>}
        </BoxFR>
      ),
    },
    {
      field: "Amnt", headerName: "จำนวน", width: 85, type: "number", align: "right", headerAlign: "right",
      renderCell: ({ row }) => (
        <Typography variant="body2" color={row.InOutTyp === "I" ? "error.main" : "text.primary"}>
          {numberFormat(row.Amnt)}
        </Typography>
      ),
    },
    {
      field: "PayTm", headerName: "งวดที่จ่าย", width: 140, sortable: false,
      renderCell: ({ row }) => {
        const movable = MOVABLE_EXP.includes(row.ExpId)
        if (locked || !movable) {
          return <Typography variant="body2">{row.SalDte ? dayjs(row.SalDte).format("MM/YYYY") : "-"}</Typography>
        }
        return (
          <DesktopDatePicker disableMaskedInput openTo="month" views={["year", "month"]} inputFormat="MM/YYYY"
            value={dayjs(row.SalDte)} onChange={(v) => handleMove(row, v)}
            renderInput={(params) => <TextField size="small" sx={{ width: 118 }} {...params} />} />
        )
      },
    },
    {
      field: "Lock", headerName: "", width: 50, align: "center", sortable: false,
      renderCell: ({ row }) => {
        if (locked) return <LockRounded fontSize="small" color="disabled" />
        if (+row.IsClaimed === 1) {
          return (
            <Tooltip title="เอาออกจากงวด (กลับเป็นค้างจ่าย)">
              <IconButton size="small" sx={{ p: 0 }} onClick={() => handleUnclaim(row)}>
                <LinkOffRounded fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          )
        }
        return (
          <IconButton size="small" sx={{ p: 0 }} onClick={() => handleDelItem(row)}>
            <DeleteRounded fontSize="small" color="action" />
          </IconButton>
        )
      },
    },
  ], [locked, handleDelItem, handleUnclaim, handleMove])

  return (
    <Dialog open={dialogOpen} PaperComponent={PaperComponent} fullWidth maxWidth="md">
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`เงินเดือน — ${drvNm || ""} (${dayjs(salDte).format("MMMM YYYY")})`} onDeleteClick={null} />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1.5 }}>
          <BoxFR sx={{ flexWrap: "wrap" }}>
            <Chip color={stsInfo.color} label={stsInfo.label} />
            {/* running balance — same figure as the payslip's top-right block */}
            <Typography variant="caption" color="text.secondary">
              {`หักเงินประกันสะสม(${slip.AccuSecDepCnt}): ${numberFormat(slip.AccuDeSecDepAmnt)} บาท`}
            </Typography>
            <Box flex={1} />
            {/* ค้างจ่าย filed into this งวด but never pulled in would otherwise short-pay the
                driver with nothing on screen to say so */}
            {payroll?.UnclaimedCnt > 0 && (
              <Tooltip placement="top"
                title={`ยังไม่ได้ดึงเข้างวดนี้ ${numberFormat(payroll.UnclaimedAmnt)} บาท`}>
                <Chip color="warning" variant="outlined" size="small"
                  label={`ค้างจ่ายงวดนี้ ${payroll.UnclaimedCnt} รายการ`} />
              </Tooltip>
            )}
            {/* the bulk build actions (ดึงเดือนก่อนหน้า / หักเบิกล่วงหน้า / คำนวณ ปกส.) are on the
                DriverSalaryV3 toolbar so they can run on a multi-row selection */}
            {sts <= 1 && <PillButton color="primary" label="ดึงรายการค้างจ่าย" onClick={() => setDialogClaimOpen(true)} />}
            {sts === 1 && <PillButton color="info" label="ยืนยัน" onClick={handleConfirm} />}
            {sts === 2 && <PillButton color="success" label="จ่าย" onClick={handlePay} />}
            {sts >= 2 && <PillButton color="warning" label="เปิดแก้ไข" onClick={handleReopen} />}
          </BoxFR>

          <BoxFR sx={{ alignItems: "stretch", gap: 1 }}>
            <SlipBox title="รายได้" rows={slip.incomeRows} footerLabel="รวมรายได้" footerAmnt={slip.SumInc} />
            <SlipBox title="รายการหัก" rows={slip.deductRows} footerLabel="รวมรายการหัก" footerAmnt={slip.SumDeduct} />
            <SlipBox title="สรุปยอด" rows={slipTotalRows(slip)} footerLabel="ยอดคงเหลือ" footerAmnt={slip.Net} />
            <SlipBox title="ข้อมูลสรุป" rows={slipDetailRows(slip)} />
          </BoxFR>

          <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>ค่าเที่ยว</Typography>
          <Box sx={{ height: 260 }}>
            <DataGridCellExpand hideFooter density="compact" rows={rows} columns={columns} />
          </Box>

          {!locked &&
            <BoxFR sx={{ mt: 1 }}>
              <TextField size="small" select sx={{ width: 180 }} label="เพิ่มรายการ"
                value={addData.ExpId} onChange={e => setAddData(o => ({ ...o, ExpId: e.target.value }))}>
                {addOptions.map(e => <MenuItem key={e.ExpId} value={e.ExpId}>{e.label}</MenuItem>)}
              </TextField>
              <TextField size="small" sx={{ width: 100 }} label="จำนวนเงิน" type="number"
                value={addData.Amnt} onChange={e => setAddData(o => ({ ...o, Amnt: e.target.value }))} />
              <TextField size="small" sx={{ flex: 1 }} label="หมายเหตุ"
                value={addData.Dscp} onChange={e => setAddData(o => ({ ...o, Dscp: e.target.value }))} />
              <IconButton color="primary" onClick={handleAdd}><AddCircleRounded /></IconButton>
            </BoxFR>}
        </BoxFC>
      </DialogContent>
      <ClaimAccrualDialog
        dialogOpen={dialogClaimOpen}
        setDialogOpen={setDialogClaimOpen}
        drvId={drvId}
        drvNm={drvNm}
        salDte={salDte}
        payrollId={payroll?.PayrollId}
        onFinish={() => { load(); if (onFinish) onFinish() }} />
    </Dialog>
  )
}

const PillButton = ({ color, label, onClick }) => (
  <Box component="button" onClick={onClick}
    sx={{ border: 0, borderRadius: 1, px: 1.5, py: 0.5, cursor: "pointer", color: "white", bgcolor: `${color}.main` }}>
    {label}
  </Box>
)

export default React.memo(PayrollDetailDialog)
